import { Box, Link, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { BLUE_TEXT, BORDER, INTER, NAVY, PAGE_BACKGROUND, POPPINS, SLATE, cardSx } from '../publicStyles';

export const accountLinkSx = { fontFamily: INTER, fontSize: 14, fontWeight: 600, color: BLUE_TEXT } as const;
export const accountBodySx = { fontFamily: INTER, fontSize: 15, lineHeight: 1.6, color: SLATE } as const;

interface AccountCardProps {
    title: string;
    intro?: ReactNode;
    width?: number;
    children: ReactNode;
    /** Short help line under the card. */
    footer?: ReactNode;
    /** Mockup-only state switcher, shown below everything else. */
    controls?: ReactNode;
}

// One layout for every public account page: a single card on the page background, with
// help text below it. The header above the card carries the brand, so the card does not repeat it.
export default function AccountCard({ title, intro, width = 440, children, footer, controls }: AccountCardProps) {
    return (
        <Box component="main" sx={{ flex: 1, backgroundColor: PAGE_BACKGROUND, px: 2, py: { xs: 4, sm: 7 } }}>
            <Box sx={{ maxWidth: width, mx: 'auto' }}>
                <Box sx={{ ...cardSx, p: { xs: 3, sm: 4 }, boxShadow: '0 10px 30px rgba(22, 58, 99, 0.06)' }}>
                    <Typography component="h1" sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 26, lineHeight: 1.25, color: NAVY }}>
                        {title}
                    </Typography>
                    {intro && <Typography sx={{ ...accountBodySx, mt: 1 }}>{intro}</Typography>}
                    {children}
                </Box>
                <Typography component="div" sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, textAlign: 'center', mt: 3 }}>
                    {footer ?? <>
                        Need help?{' '}
                        <Link href="mailto:support@verbaschola.com" underline="hover" sx={{ ...accountLinkSx, fontSize: 13 }}>support@verbaschola.com</Link>
                    </>}
                </Typography>
                {controls}
            </Box>
        </Box>
    );
}

export interface MockupStateOption {
    label: string;
    to: string;
}

// Lists each state of a page as a link, so every state has its own reviewable URL.
export function MockupStateLinks({ options, current }: { options: MockupStateOption[]; current: string }) {
    return (
        <Box component="aside" aria-label="Mockup controls" sx={{ mt: 4, p: 2, border: `1px dashed ${BORDER}`, borderRadius: '14px' }}>
            <Typography sx={{ fontFamily: INTER, fontSize: 12, fontWeight: 700, color: SLATE, mb: 1 }}>Mockup state (not part of the design)</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {options.map(option => (
                    <Link key={option.to} component={RouterLink} to={option.to} underline="hover"
                        aria-current={option.to === current ? 'page' : undefined}
                        sx={{ ...accountLinkSx, fontSize: 13, minHeight: 32, display: 'inline-flex', alignItems: 'center',
                            ...(option.to === current ? { color: NAVY, textDecoration: 'underline' } : {}) }}>
                        {option.label}
                    </Link>
                ))}
            </Stack>
        </Box>
    );
}
